/**
 * 领域枚举与共享类型（前后端唯一真源）
 *
 * 约定：所有联合类型都从 `as const` 数组派生，运行时可用于校验与渲染选项，
 * 编译期提供字面量联合，避免前后端各写一份枚举导致漂移。
 */

/** 用户角色。teacher/admin 的完整能力在阶段 7 落地，此处先留位。 */
export const ROLES = ['student', 'teacher', 'admin'] as const;
export type Role = (typeof ROLES)[number];

/** 账号状态。deleted 为软删除，配合隐私政策中的数据删除入口。 */
export const USER_STATUSES = ['active', 'disabled', 'deleted'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** CEFR 等级，从低到高。 */
export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type CefrLevel = (typeof CEFR_LEVELS)[number];

/** 生词本中一个词条的状态。 */
export const WORD_STATUSES = ['new', 'fuzzy', 'unknown', 'known', 'mastered'] as const;
export type WordStatus = (typeof WORD_STATUSES)[number];

/** 间隔重复的记忆阶段（SM-2 变体）。 */
export const SRS_STATES = ['new', 'learning', 'review', 'lapsed'] as const;
export type SrsState = (typeof SRS_STATES)[number];

/**
 * 复习自评。0 忘记 / 1 困难 / 2 模糊 / 3 记得 / 4 秒答。
 * 阶段 4 的调度算法直接消费该枚举。
 */
export const SRS_RATINGS = [0, 1, 2, 3, 4] as const;
export type SrsRating = (typeof SRS_RATINGS)[number];

export const SRS_RATING_LABELS: Record<SrsRating, string> = {
  0: '完全忘记',
  1: '很困难',
  2: '有点模糊',
  3: '记得',
  4: '秒答',
};

/** 文章可见性：public 收录进公共目录，private 仅作者本人可见。 */
export const ARTICLE_VISIBILITIES = ['public', 'private'] as const;
export type ArticleVisibility = (typeof ARTICLE_VISIBILITIES)[number];

/**
 * 版权状态。takedown 用于权利人投诉后立即下架。
 * 平台不内置受版权保护的教材全文，正文一律由有权限的用户导入并留痕。
 */
export const COPYRIGHT_STATUSES = [
  'public_domain',
  'public_metadata',
  'licensed',
  'user_authorized',
  'user_imported',
  'takedown',
] as const;
export type CopyrightStatus = (typeof COPYRIGHT_STATUSES)[number];

export const COPYRIGHT_STATUS_LABELS: Record<CopyrightStatus, string> = {
  public_domain: '公版作品',
  public_metadata: '仅公开目录元数据',
  licensed: '已获授权',
  user_authorized: '用户声明拥有授权',
  user_imported: '用户导入',
  takedown: '已下架',
};

/** 文章进入平台的方式。 */
export const IMPORT_SOURCES = ['builtin', 'paste', 'upload', 'url'] as const;
export type ImportSource = (typeof IMPORT_SOURCES)[number];

/** 教材/单元的归属方。 */
export const OWNER_TYPES = ['platform', 'user'] as const;
export type OwnerType = (typeof OWNER_TYPES)[number];

/** AI 缓存的命名空间，决定缓存键前缀与后台筛选维度。 */
export const AI_NAMESPACES = [
  'sense',
  'sentence_analysis',
  'level_summary',
  'advice',
  'translate',
] as const;
export type AiNamespace = (typeof AI_NAMESPACES)[number];

/** AI 提供方。mock 用于无密钥的本地开发与测试。 */
export const AI_PROVIDERS = ['workers-ai', 'openai-compat', 'mock'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

/** 文章分句/切分状态。导入后需要把正文切成句子，异步完成。 */
export const SEGMENT_STATUSES = ['pending', 'processing', 'done'] as const;
export type SegmentStatus = (typeof SEGMENT_STATUSES)[number];

/** AI 缓存条目状态。error 也会落库，便于后台看到失败样本并重试。 */
export const AI_CACHE_STATUSES = ['ok', 'error'] as const;
export type AiCacheStatus = (typeof AI_CACHE_STATUSES)[number];

/** 埋点事件类型。 */
export const EVENT_TYPES = [
  'lookup',
  'mark',
  'review',
  'read',
  'import',
  'auth',
  'ai_call',
  'error',
  'export',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** 学习模式，阶段 7 的学习方法推荐按此维度产出建议。 */
export const STUDY_MODES = [
  'intensive',
  'extensive',
  'shadowing',
  'backtranslation',
  'retell',
] as const;
export type StudyMode = (typeof STUDY_MODES)[number];

export const STUDY_MODE_LABELS: Record<StudyMode, string> = {
  intensive: '精读',
  extensive: '泛读',
  shadowing: '影子跟读',
  backtranslation: '回译',
  retell: '复述',
};

// ---------------------------------------------------------------------------
// 实体视图（对外暴露的子集，绝不含 password_hash 等敏感字段）
// ---------------------------------------------------------------------------

export interface UserPublic {
  id: string;
  email: string | null;
  displayName: string;
  role: Role;
  status: UserStatus;
  currentCefr: CefrLevel | null;
  settings: UserSettings;
  createdAt: number;
}

export interface UserSettings {
  theme: 'light' | 'dark' | 'system';
  readerFontSize: number;
  readerLineHeight: number;
  defaultStudyMode: StudyMode;
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  theme: 'system',
  readerFontSize: 18,
  readerLineHeight: 1.9,
  defaultStudyMode: 'intensive',
};

/** 统计摘要，用于 /api/me 与个人中心顶部卡片。 */
export interface UserStatsSummary {
  wordCount: number;
  dueToday: number;
  currentCefr: CefrLevel | null;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * 教材 / 文章 / 生词本 / 学习建议的对外视图。
 *
 * 单独放一个文件只是因为体量：枚举与用户模型在上面，接口形状在下面。
 * 两者之间只有类型引用，`import type` 在编译后会被完全擦除，不会形成运行时循环依赖。
 */
export * from './content';
export * from './study';
